import { createRoot } from "react-dom/client";
import type { ComponentType } from "react";

/**
 * Island mounting.
 *
 * PHP templates drop a placeholder like:
 *   <div data-ozria-component="assessment"></div>
 *   <div data-ozria-component="count-up" data-value="98" data-suffix="%"></div>
 *
 * Components are loaded lazily on first intersection so the bundle never blocks
 * first paint - most visitors never scroll to the runway chart.
 */

/**
 * Each island declares its own props, so the registry is deliberately loose -
 * values arrive as strings from data attributes and cannot be checked
 * statically. Components coerce and validate their own inputs (see CountUp’s
 * `num()` and ReadinessAssessment’s `parseTiers()`).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Loader = () => Promise<{ default: ComponentType<any> }>;

const registry: Record<string, Loader> = {
  assessment: () => import("./ReadinessAssessment"),
  "month-end-close": () => import("./MonthEndClosePanel"),
  "cash-runway": () => import("./CashRunwayChart"),
  "count-up": () => import("./CountUp"),
};

/** data-foo-bar="1" -> { fooBar: "1" }, minus the component key itself. */
function propsFrom(el: HTMLElement): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const { name, value } of Array.from(el.attributes)) {
    if (!name.startsWith("data-") || name === "data-ozria-component") continue;
    const key = name
      .slice(5)
      .replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    props[key] = value;
  }
  return props;
}

async function mount(el: HTMLElement) {
  const key = el.dataset.ozriaComponent;
  if (!key) return;

  const loader = registry[key];
  if (!loader) {
    console.warn(`[ozria] Unknown island "${key}"`);
    return;
  }

  try {
    const { default: Component } = await loader();
    createRoot(el).render(<Component {...propsFrom(el)} />);
  } catch (err) {
    // A failed island must never take the page down. The surrounding content
    // is server-rendered and stays readable.
    console.error(`[ozria] Failed to mount "${key}"`, err);
  }
}

function init() {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>("[data-ozria-component]")
  );
  if (!nodes.length) return;

  if (!("IntersectionObserver" in window)) {
    nodes.forEach(mount);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        mount(entry.target as HTMLElement);
      }
    },
    { rootMargin: "200px" }
  );

  // The assessment is the page's lead tool and the #assessment links jump
  // straight to it, so it mounts at once rather than on approach.
  nodes.forEach((node) =>
    node.dataset.ozriaComponent === "assessment" ? mount(node) : observer.observe(node)
  );
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
